import React, { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { Document, Page, View, Text, StyleSheet, Font } from '@react-pdf/renderer'
import dayjs from 'dayjs'
import { useIsTimesheetApproved } from '../../../../../utils/hooks/approvedTimesheet'
import {
	ServiceExecution,
	ServiceOriginal,
	TimeSheetPageState,
} from '../../../../../store/slices/timesheet/timesheetPageSlice'
import { ServiceExecutionUpdateRequest } from '../../../../../store/slices/timesheet/timesheetApprovalDetails/timesheetApprovalDetailsSlice'
import { TimesheetStoreState } from '../../../../../store/timesheetStore'

// Registrar fonte Poppins usando arquivos locais
// IMPORTANTE: Você precisa baixar os arquivos TTF da fonte Poppins e colocá-los em public/fonts/
// Baixe de: https://fonts.google.com/specimen/Poppins (clique em "Download family")
// Extraia os arquivos TTF e coloque-os em: public/fonts/
// Arquivos necessários:
// - Poppins-Regular.ttf (weight 400)
// - Poppins-Medium.ttf (weight 500)
// - Poppins-SemiBold.ttf (weight 600)
// - Poppins-Bold.ttf (weight 700)
// Flag global para indicar se a fonte Poppins foi registrada com sucesso
// Usada para decidir qual fonte usar nos estilos (Poppins ou Helvetica como fallback)
let poppinsRegistered = false
try {
	// Registra a família de fontes Poppins com todas as variações necessárias
	Font.register({
		family: 'Poppins',
		fonts: [
			{
				src: '/fonts/Poppins-Regular.ttf',
				fontWeight: 400,
				fontStyle: 'normal',
			},
			{
				src: '/fonts/Poppins-Medium.ttf',
				fontWeight: 500,
				fontStyle: 'normal',
			},
			{
				src: '/fonts/Poppins-SemiBold.ttf',
				fontWeight: 600,
				fontStyle: 'normal',
			},
			{
				src: '/fonts/Poppins-Bold.ttf',
				fontWeight: 700,
				fontStyle: 'normal',
			},
			// Versões itálicas
			{
				src: '/fonts/Poppins-Italic.ttf',
				fontWeight: 400,
				fontStyle: 'italic',
			},
			{
				src: '/fonts/Poppins-Italic.ttf',
				fontWeight: 500,
				fontStyle: 'italic',
			},
			{
				src: '/fonts/Poppins-Italic.ttf',
				fontWeight: 600,
				fontStyle: 'italic',
			},
			{
				src: '/fonts/Poppins-BoldItalic.ttf',
				fontWeight: 700,
				fontStyle: 'italic',
			},
		],
	})
	// Marca como registrado apenas se não houver erro
	poppinsRegistered = true
} catch (error) {
	// Em caso de erro, loga aviso e mantém flag como false
	// O código usará Helvetica como fallback automaticamente
	console.warn(
		'Erro ao registrar fonte Poppins. Certifique-se de que os arquivos TTF estão em public/fonts/. Usando Helvetica como fallback.',
		error,
	)
}

/**
 * Interface que representa um tipo de trabalho (WorkType).
 * Usado para identificar e descrever diferentes regimes de trabalho.
 */
interface WorkType {
	id: number
	value: string
}

// Estilos para o PDF
const styles = StyleSheet.create({
	// Estilo base da página - define padding, fonte padrão e cor de fundo
	page: {
		padding: 30,
		fontFamily: poppinsRegistered ? 'Poppins' : 'Helvetica',
		fontSize: 10,
		backgroundColor: '#FFFFFF',
	},
	// Header Styles - Estilos do cabeçalho do documento
	// Container principal do cabeçalho - exibe informações do motorista e data em linha horizontal
	header: {
		flexDirection: 'row',
		justifyContent: 'space-between',
		marginBottom: 30,
		padding: 15,
		paddingBottom: 15,
		backgroundColor: '#F5F5F5',
		borderRadius: 5,
	},
	// Container esquerdo do cabeçalho - contém nome e informações do motorista
	headerLeft: {
		flex: 1,
	},
	// Nome do motorista - texto em destaque no cabeçalho
	motoristaNome: {
		fontSize: 12,
		fontWeight: 600,
		marginBottom: 10,
		color: '#333333',
		fontFamily: poppinsRegistered ? 'Poppins' : 'Helvetica',
		lineHeight: 1.3,
		letterSpacing: 0,
	},
	// Informações do motorista - número interno e empresa
	motoristaInfo: {
		fontSize: 7,
		fontWeight: 400,
		color: '#000000',
		lineHeight: 1.3,
		letterSpacing: 0,
		margin: 0,
		fontFamily: poppinsRegistered ? 'Poppins' : 'Helvetica',
	},
	// Container direito do cabeçalho - alinha a data à direita
	headerRight: {
		textAlign: 'right',
	},
	// Data da folha de ponto - exibida no cabeçalho à direita
	data: {
		fontSize: 12,
		fontWeight: 600,
		color: '#333333',
		fontFamily: poppinsRegistered ? 'Poppins' : 'Helvetica',
		lineHeight: 1.3,
		letterSpacing: 0,
	},
	// Section Styles - Estilos das seções do documento
	// Container genérico de seção - espaçamento inferior padrão
	section: {
		marginBottom: 15,
	},
	// Título principal de seção - usado para "Serviços", "Folha de Ponto", etc.
	sectionTitle: {
		fontSize: 12,
		fontWeight: 600,
		marginBottom: 15,
		color: '#000000',
		textTransform: 'uppercase',
		letterSpacing: 0,
		lineHeight: 1.3,
		fontFamily: poppinsRegistered ? 'Poppins' : 'Helvetica',
	},
	// Título secundário de seção - usado para subseções como "Regime de Trabalho"
	sectionTitleRegime: {
		fontSize: 9,
		fontWeight: 600,
		marginBottom: 2,
		color: '#000000',
		textTransform: 'uppercase',
		letterSpacing: 0,
		lineHeight: 1.3,
		fontFamily: poppinsRegistered ? 'Poppins' : 'Helvetica',
	},
	// Seção unificada - agrupa Folha de Ponto, Regime, Abonos e Notas com bordas superior e inferior
	unifiedSection: {
		borderTopWidth: 0.5,
		borderTopColor: '#000000',
		borderBottomWidth: 0.5,
		borderBottomColor: '#000000',
		paddingTop: 2,
	},
	// Item dentro da seção unificada - espaçamento entre itens
	unifiedSectionItem: {
		marginBottom: 3,
	},
	// Table Styles - Estilos das tabelas
	// Container da tabela - largura total e espaçamento inferior
	table: {
		width: '100%',
		marginBottom: 2,
		backgroundColor: '#FFFFFF',
	},
	// Linha da tabela - exibe dados em linha horizontal com borda inferior para separação
	tableRow: {
		flexDirection: 'row',
		borderBottomWidth: 0.4,
		borderBottomColor: '#4D4D4D',
		backgroundColor: '#FFFFFF',
		paddingTop: 5,
		paddingBottom: 5,
	},
	// Última linha da tabela - sem borda inferior para não criar linha extra no final
	tableRowLast: {
		flexDirection: 'row',
		backgroundColor: '#FFFFFF',
		borderBottomWidth: 0,
	},
	// Cabeçalho da tabela - células do cabeçalho com texto em negrito
	tableHeader: {
		fontWeight: 600,
		fontSize: 7,
		paddingTop: 5,
		paddingBottom: 5,
		paddingLeft: 5,
		paddingRight: 5,
		textAlign: 'left',
		color: '#000000',
		fontFamily: poppinsRegistered ? 'Poppins' : 'Helvetica',
		lineHeight: 1.38,
		letterSpacing: 0.07,
	},
	// Última célula do cabeçalho - placeholder para estilos específicos se necessário
	tableHeaderLast: {
		// Sem bordas verticais
	},
	// Célula padrão da tabela - alinhamento à esquerda, usado para dados gerais
	tableCell: {
		fontSize: 7,
		fontWeight: 400,
		color: '#000000',
		fontFamily: poppinsRegistered ? 'Poppins' : 'Helvetica',
		lineHeight: 1.3,
		letterSpacing: 0,
		paddingLeft: 5,
	},
	// Última célula da linha - placeholder para estilos específicos se necessário
	tableCellLast: {
		// Sem bordas verticais
	},
	// Estilos específicos para células de Serviços
	// Célula com texto centralizado - usado para valores numéricos e status
	tableCellCenter: {
		fontSize: 7,
		fontWeight: 400,
		textAlign: 'center',
		color: '#000000',
		fontFamily: poppinsRegistered ? 'Poppins' : 'Helvetica',
		lineHeight: 1.3,
		letterSpacing: 0,
	},
	// Célula com texto alinhado à direita - usado para valores numéricos como KMs
	tableCellRight: {
		fontSize: 7,
		fontWeight: 400,
		textAlign: 'right',
		color: '#000000',
		fontFamily: poppinsRegistered ? 'Poppins' : 'Helvetica',
		lineHeight: 1.3,
		letterSpacing: 0,
	},
	// Total Horas - Estilos para exibição do total de horas trabalhadas
	// Label do total de horas - texto em negrito
	totalHoras: {
		fontSize: 7,
		marginTop: 5,
		textAlign: 'left',
		fontWeight: 600,
		fontFamily: poppinsRegistered ? 'Poppins' : 'Helvetica',
		lineHeight: 1.3,
		letterSpacing: 0.07,
		color: '#000000',
	},
	// Valor do total de horas - texto normal após o label
	totalHorasValue: {
		fontSize: 7,
		fontWeight: 400,
		fontFamily: poppinsRegistered ? 'Poppins' : 'Helvetica',
		lineHeight: 1.3,
		letterSpacing: 0.07,
		color: '#000000',
	},
	// Regime Info - Estilos para informações do regime de trabalho
	// Container horizontal para regime de trabalho e agente único
	regimeInfo: {
		flexDirection: 'row',
		fontSize: 7,
		fontFamily: poppinsRegistered ? 'Poppins' : 'Helvetica',
		lineHeight: 1.3,
		letterSpacing: 0.07,
		color: '#000000',
	},
	// Label do item de regime - texto em negrito (ex: "Regime de Trabalho:")
	regimeItem: {
		fontSize: 7,
		fontWeight: 600,
		fontFamily: poppinsRegistered ? 'Poppins' : 'Helvetica',
		lineHeight: 1.3,
		letterSpacing: 0.07,
		color: '#000000',
	},
	// Valor do item de regime - texto normal após o label
	regimeItemValue: {
		fontSize: 7,
		fontWeight: 400,
		fontFamily: poppinsRegistered ? 'Poppins' : 'Helvetica',
		lineHeight: 1.3,
		letterSpacing: 0.07,
		color: '#000000',
	},
	// Abonos Content - Estilos para abonos e refeições
	// Container principal de abonos - define fonte padrão
	abonosContent: {
		fontFamily: poppinsRegistered ? 'Poppins' : 'Helvetica',
	},
	// Container de refeições - exibe códigos de refeição em linha com quebra
	refeicoes: {
		marginBottom: 1,
		flexDirection: 'row',
		flexWrap: 'wrap',
	},
	// Item de refeição - código e descrição de cada refeição
	refeicaoItem: {
		fontSize: 7,
		fontWeight: 400,
		fontFamily: poppinsRegistered ? 'Poppins' : 'Helvetica',
		lineHeight: 1.3,
		letterSpacing: 0.07,
		color: '#000000',
	},
	// Container de outros abonos - espaçamento superior para separar de refeições
	outrosAbonos: {
		marginTop: 1,
	},
	// Lista de abonos - espaçamento superior para separar seções
	abonosList: {
		marginTop: 8,
	},
	// Label do abono - texto em negrito (ex: "Outros Abonos:")
	abonoItem: {
		fontSize: 7,
		fontWeight: 600,
		marginBottom: 0,
		fontFamily: poppinsRegistered ? 'Poppins' : 'Helvetica',
		lineHeight: 1.3,
		letterSpacing: 0.07,
		color: '#000000',
	},
	// Valor do abono - texto normal com descrição e valor
	abonoValue: {
		fontSize: 7,
		fontWeight: 400,
		fontFamily: poppinsRegistered ? 'Poppins' : 'Helvetica',
		lineHeight: 1.3,
		letterSpacing: 0.07,
		color: '#000000',
	},
	// Notas and Comentários - Estilos para notas e comentários
	// Label das notas adicionais - texto em negrito
	notasContent: {
		fontSize: 7,
		fontWeight: 600,
		lineHeight: 1.3,
		letterSpacing: 0,
		fontFamily: poppinsRegistered ? 'Poppins' : 'Helvetica',
		color: '#000000',
	},
	// Valor das notas adicionais - texto normal com o conteúdo das notas
	notasValue: {
		fontSize: 7,
		fontWeight: 400,
		lineHeight: 1.3,
		letterSpacing: 0,
		fontFamily: poppinsRegistered ? 'Poppins' : 'Helvetica',
		color: '#000000',
	},
	// Label dos comentários do escalador - texto em negrito
	comentariosContent: {
		fontSize: 7,
		fontWeight: 600,
		lineHeight: 1.3,
		letterSpacing: 0,
		fontFamily: poppinsRegistered ? 'Poppins' : 'Helvetica',
		color: '#000000',
	},
	// Valor dos comentários do escalador - texto normal com o conteúdo dos comentários
	comentariosValue: {
		fontSize: 7,
		fontWeight: 400,
		lineHeight: 1.3,
		letterSpacing: 0,
		fontFamily: poppinsRegistered ? 'Poppins' : 'Helvetica',
		color: '#000000',
	},
	// Notas dentro da tabela - Estilos para notas adicionais exibidas dentro das linhas da tabela
	// Célula de notas adicionais - estilo itálico para destacar comentários em serviços
	notasAdicionais: {
		//backgroundColor: '#F9F9F9',
		padding: 10,
		fontStyle: 'italic',
		fontFamily: poppinsRegistered ? 'Poppins' : 'Helvetica',
	},
	// Seção com altura mínima para evitar quebra de página no meio
	// Previne que seções importantes sejam divididas entre páginas
	minHeightSection: {
		minHeight: 100,
	},
	// Footer Styles - Estilos do rodapé do documento
	// Rodapé com numeração de páginas - posicionado fixo no canto inferior direito
	footer: {
		position: 'absolute',
		bottom: 20,
		right: 30,
		fontSize: 6,
		fontWeight: 400,
		color: '#666666',
		textAlign: 'right',
		fontFamily: poppinsRegistered ? 'Poppins' : 'Helvetica',
		letterSpacing: 0,
	},
})

/**
 * Interface que representa os parâmetros de gratificação da empresa.
 * Usado para processar e exibir abonos adicionais na folha de ponto.
 */
interface CompanyGratificationParam {
	id: number
	limite: number
	descricao: string
	campoSP: string
	valor: number
}

/**
 * Componente principal para renderização do documento PDF da folha de ponto.
 * Gera um PDF formatado com informações do motorista, serviços executados,
 * folha de ponto, regime de trabalho, abonos e notas adicionais.
 *
 * @param timesheetPage - Estado completo da folha de ponto
 * @param servicesOriginal - Array de serviços originais escalados
 * @param servicesExecution - Array de serviços de execução
 * @param servicesApproval - Array de serviços de aprovação
 * @param isTimesheetApproved - Flag indicando se a folha de ponto está aprovada
 * @param workTypes - Array opcional de tipos de trabalho disponíveis
 * @param companyGratifications - Array opcional de gratificações da empresa
 */
export const PrintDocument: React.FC<{
	timesheetPage: TimeSheetPageState
	servicesOriginal: ServiceOriginal[]
	servicesExecution: ServiceExecution[]
	servicesApproval: ServiceExecutionUpdateRequest[]
	isTimesheetApproved: boolean
	workTypes?: WorkType[]
	companyGratifications?: CompanyGratificationParam[]
}> = ({
	timesheetPage,
	servicesOriginal,
	servicesExecution,
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	servicesApproval: _servicesApproval,
	isTimesheetApproved,
	workTypes,
	companyGratifications,
}) => {
	// Valor fixo para "Agente Único" - campo obrigatório no PDF
	const agenteUnico = '00:00:00'

	/**
	 * Calcula a quilometragem total de um serviço baseado nos veículos associados.
	 * Soma a diferença entre a quilometragem final e inicial de cada veículo.
	 *
	 * @param service - Serviço original ou de execução contendo informações de veículos
	 * @returns Total de quilômetros percorridos (0 se não houver veículos)
	 */
	const calculateKms = (service: ServiceOriginal | ServiceExecution) => {
		return service?.kmsVehicles?.reduce((acc, curr) => acc + curr.kmsEnd - curr.kmsIni, 0) || 0
	}

	/**
	 * Calcula a quilometragem por modo de operação (carga ou vazio).
	 * Para ServiceOriginal, utiliza os campos kmsFull e kmsEmpty diretamente.
	 * Para ServiceExecution, calcula a partir dos veículos filtrando por inEmpty.
	 *
	 * @param service - Serviço original ou de execução
	 * @param inEmpty - Se true, calcula KMs vazio; se false, calcula KMs carga
	 * @returns Total de quilômetros no modo especificado
	 */
	const calculateKmsByMode = (service: ServiceOriginal | ServiceExecution, inEmpty: boolean) => {
		if (service && 'kmsFull' in service && 'kmsEmpty' in service) {
			const s = service as ServiceOriginal
			const kms = s?.kmsVehicles || []
			return kms?.reduce((acc, kv) => (kv.inEmpty === inEmpty ? acc + (kv.kmsEnd - kv.kmsIni) : acc), 0) || 0
		}
		// ServiceExecution - calcular a partir de kmsVehicles
		const vehicles = service?.kmsVehicles || []
		return vehicles.reduce((acc, kv) => {
			if (kv.inEmpty === inEmpty) {
				return acc + (kv.kmsEnd - kv.kmsIni)
			}
			return acc
		}, 0)
	}

	/**
	 * Calcula a quilometragem total de um serviço.
	 * Para ServiceOriginal, soma kmsFull e kmsEmpty.
	 * Para ServiceExecution, utiliza a função calculateKms que soma todos os veículos.
	 *
	 * @param service - Serviço original ou de execução
	 * @returns Total de quilômetros (carga + vazio)
	 */
	const calculateKmsTotal = (service: ServiceOriginal | ServiceExecution) => {
		if (service && 'kmsFull' in service && 'kmsEmpty' in service) {
			const s = service as ServiceOriginal
			return calculateKms(s)
		}
		return calculateKms(service)
	}

	/**
	 * Obtém o valor (descrição) do tipo de trabalho (WorkType) atual.
	 * Se a folha de ponto estiver aprovada, busca o workTypeId da aprovação.
	 * Caso contrário, busca o workTypeId da folha de ponto.
	 *
	 * @returns Descrição do tipo de trabalho ou undefined se não encontrado
	 */
	const getWorktype = (): string | undefined => {
		if (!workTypes || workTypes.length === 0) {
			return undefined
		}

		if (isTimesheetApproved) {
			return workTypes.find((workType: WorkType) => workType.id === timesheetPage.approval?.workTypeId)?.value
		}
		return workTypes.find((workType: WorkType) => workType.id === timesheetPage.workTypeId)?.value
	}

	/**
	 * Verifica se um serviço de execução está conforme com o serviço original escalado.
	 * Um serviço é considerado conforme quando:
	 * - A data/hora de início é exatamente igual (mesmo minuto)
	 * - A data/hora de término não é anterior à original
	 * - A data/hora de término não excede 1 hora além da original
	 *
	 * @param service - Serviço original escalado
	 * @param serviceExecution - Serviço de execução a ser validado (opcional)
	 * @returns true se o serviço estiver conforme, false caso contrário
	 */
	const isServiceConforme = (service: ServiceOriginal, serviceExecution?: ServiceExecution): boolean => {
		if (!serviceExecution) {
			return true
		}

		// Comparar startDateTime - devem ser iguais
		const serviceStart = dayjs(service.startDateTime)
		const executionStart = dayjs(serviceExecution?.startDateTime)

		if (!serviceStart.isValid() || !executionStart.isValid()) {
			return false
		}

		// startDateTime deve ser igual
		if (!serviceStart.isSame(executionStart, 'minute')) {
			return false
		}

		// Comparar endDateTime - o serviceExecution pode ser no máximo 1 hora a mais
		const serviceEnd = dayjs(service.endDateTime)
		const executionEnd = dayjs(serviceExecution.endDateTime)

		if (!serviceEnd.isValid() || !executionEnd.isValid()) {
			return false
		}

		// endDateTime do serviceExecution não pode ser menor que o do service
		if (executionEnd.isBefore(serviceEnd)) {
			return false
		}

		// endDateTime do serviceExecution pode ser no máximo 1 hora a mais
		const diffInHours = executionEnd.diff(serviceEnd, 'hour', true)
		if (diffInHours > 1) {
			return false
		}

		return true
	}

	/**
	 * Calcula o total de horas trabalhadas e formata em HH:mm.
	 * Usa useMemo para otimizar o cálculo, recalculando apenas quando workTimes mudar.
	 *
	 * @returns String formatada no padrão HH:mm (ex: "08:30")
	 */
	const totalHorasFormatado = useMemo(() => {
		// Calcula o total de horas trabalhadas somando a diferença entre
		// início e fim de cada período de trabalho
		const totalHours =
			timesheetPage.workTimes?.reduce(
				(acc, item) => acc + dayjs(item.endTime).diff(dayjs(item.startTime), 'hour', true),
				0,
			) || 0

		// Converte horas decimais para formato HH:mm
		const hours = Math.floor(totalHours)
		const minutes = Math.round((totalHours - hours) * 60)
		return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`
	}, [timesheetPage.workTimes])

	/**
	 * Renderiza as linhas da tabela de períodos de trabalho.
	 * Mapeia os períodos de trabalho e exibe informações formatadas.
	 *
	 * @param workTimes - Array de períodos de trabalho
	 * @returns Array de elementos View representando as linhas da tabela
	 */
	const renderWorkTimesRows = (workTimes: typeof timesheetPage.workTimes) => {
		if (!workTimes) return null

		return workTimes.map((item, index) => (
			<View
				key={index}
				style={styles.tableRow}
				wrap={false}
			>
				<Text style={[styles.tableCell, { flex: 1 }]}>{'Periodo de trab. ' + (index + 1)}</Text>
				<Text style={[styles.tableCell, { flex: 1 }]}>{dayjs(item.startTime).format('HH:mm')}</Text>
				<Text style={[styles.tableCell, { flex: 1 }]}>{dayjs(item.startTime).format('DD/MM/YYYY')}</Text>
				<Text style={[styles.tableCell, { flex: 1 }]}>{dayjs(item.endTime).format('HH:mm')}</Text>
				<Text style={[styles.tableCell, { flex: 1 }]}>{dayjs(item.endTime).format('DD/MM/YYYY')}</Text>
				<Text style={[styles.tableCell, { flex: 1 }]}>{'-'}</Text>
				<Text style={[styles.tableCell, { flex: 1 }, styles.tableCellLast]}>{item.isClean ? 'Sim' : 'Não'}</Text>
			</View>
		))
	}

	/**
	 * Renderiza os códigos de refeições (bonus codes).
	 * Agrupa os códigos em grupos de no máximo 4 por linha com alinhamento tabulado.
	 * Mapeia os códigos de bônus e exibe código e descrição formatados.
	 *
	 * @param bonusCodes - Array de códigos de bônus
	 * @returns Array de elementos View representando as linhas de códigos de refeições
	 */
	const renderBonusCodes = (bonusCodes: typeof timesheetPage.bonusCodes) => {
		if (!bonusCodes) return null

		// Agrupa os códigos em grupos de 4
		const groups: (typeof bonusCodes)[] = []
		for (let i = 0; i < bonusCodes.length; i += 4) {
			groups.push(bonusCodes.slice(i, i + 4))
		}

		return groups.map((group, groupIndex) => (
			<View
				key={groupIndex}
				style={styles.refeicoes}
			>
				{group.map((bonusCode, index) => (
					<Text
						key={index}
						style={[styles.refeicaoItem, { flex: 1, marginRight: 15, minWidth: '20%' }]}
					>
						{bonusCode.code} - {bonusCode.description}
					</Text>
				))}
				{/* Preenche espaços vazios para manter alinhamento quando há menos de 4 itens */}
				{Array.from({ length: 4 - group.length }).map((_, fillIndex) => (
					<Text
						key={`fill-${fillIndex}`}
						style={[styles.refeicaoItem, { flex: 1, marginRight: 15, minWidth: '20%' }]}
					/>
				))}
			</View>
		))
	}

	// Verifica se há comentários do escalador para exibir a seção
	const hasEscaladorComments = (timesheetPage.approval?.comments ?? '') !== ''

	return (
		<Document>
			<Page
				size='A4'
				style={styles.page}
				wrap
			>
				{/* Header */}
				<View style={styles.header}>
					<View style={styles.headerLeft}>
						<Text style={styles.motoristaNome}>{timesheetPage?.driver?.driverName || 'N/A'}</Text>
						<Text style={styles.motoristaInfo}>
							Motorista nr {timesheetPage?.driver?.internalNumber || 'N/A'} -{' '}
							{timesheetPage?.driver?.companyName || 'N/A'}
						</Text>
					</View>
					<View style={styles.headerRight}>
						<Text style={styles.data}>{dayjs(timesheetPage?.timeSheetDate).format('DD/MM/YYYY')}</Text>
					</View>
				</View>

				{/* Serviços Section */}
				<View style={[styles.section, styles.minHeightSection]}>
					<Text style={styles.sectionTitle}>Serviços</Text>
					<View style={styles.table}>
						{/* Table Header */}
						<View
							style={styles.tableRow}
							wrap={false}
						>
							<Text style={[styles.tableHeader, { flex: 3 }]}>Início e Término{'\n'}(Escalado)</Text>
							<Text style={[styles.tableHeader, { flex: 2 }]}>Hora e Data Início{'\n'}Hora e Data Fim</Text>
							<Text style={[styles.tableHeader, { flex: 1, textAlign: 'center' }]}>Conforme</Text>
							<Text style={[styles.tableHeader, { flex: 1, textAlign: 'center' }]}>Kms Total</Text>
							<Text style={[styles.tableHeader, { flex: 1, textAlign: 'right' }]}>Kms Carga{'\n'}Kms Vazio</Text>
							<Text style={[styles.tableHeader, { flex: 1, textAlign: 'center' }]}>Passageiros</Text>
							<Text style={[styles.tableHeader, { flex: 1, textAlign: 'center' }, styles.tableHeaderLast]}>
								Limpeza
							</Text>
						</View>
						{/* Table Rows */}
						{servicesOriginal.map((service, index) => {
							// Determina se é o último serviço e se possui comentários
							// Útil para aplicar estilos diferentes na última linha da tabela
							const isLastService = index === servicesOriginal.length - 1
							const hasComments = (service.comments ?? '') !== ''
							const isLastRow = isLastService

							// Busca o serviço de execução correspondente ao serviço original
							// usando o serviceId como chave de correspondência
							const serviceExecution = servicesExecution.find(s => s.serviceId === service.serviceId)

							return (
								<React.Fragment key={index}>
									<View
										style={isLastRow || hasComments ? styles.tableRowLast : styles.tableRow}
										wrap={false}
									>
										<View
											style={[
												{
													flex: 3,
													justifyContent: 'center',
													paddingTop: 5,
													paddingBottom: 5,
													paddingLeft: 5,
													paddingRight: 5,
													backgroundColor: '#FFFFFF',
												},
											]}
										>
											<Text style={styles.tableCell}>
												<Text wrap={false}>{service.origin}</Text>
												{'\n'}
												<Text wrap={false}>{service.destination}</Text>
											</Text>
										</View>
										{/* 
                      Exibe data/hora de início e fim do serviço.
                      Se a folha estiver aprovada, usa os dados do serviceExecution (execução real).
                      Caso contrário, usa os dados do service original (escalado).
                    */}
										<View
											style={[
												{
													flex: 2,
													justifyContent: 'center',
													paddingTop: 5,
													paddingBottom: 5,
													paddingLeft: 5,
													paddingRight: 5,
													backgroundColor: '#FFFFFF',
												},
											]}
										>
											<Text style={styles.tableCell}>
												{dayjs(!isTimesheetApproved ? service?.startDateTime : serviceExecution?.startDateTime).format(
													'HH:mm',
												)}
												{'  '}
												{dayjs(!isTimesheetApproved ? service?.startDateTime : serviceExecution?.startDateTime).format(
													'DD/MM/YYYY',
												)}
												{'  '}
												{'\n'}
												{dayjs(!isTimesheetApproved ? service?.endDateTime : serviceExecution?.endDateTime).format(
													'HH:mm',
												)}
												{'  '}
												{dayjs(!isTimesheetApproved ? service?.endDateTime : serviceExecution?.endDateTime).format(
													'DD/MM/YYYY',
												)}
												{'  '}
											</Text>
										</View>
										<View
											style={[
												{
													flex: 1,
													justifyContent: 'center',
													paddingTop: 5,
													paddingBottom: 5,
													paddingLeft: 5,
													paddingRight: 5,
													backgroundColor: '#FFFFFF',
												},
											]}
										>
											<Text style={styles.tableCellCenter}>
												{isServiceConforme(service, serviceExecution) ? 'Sim' : 'Não'}
											</Text>
										</View>
										{/* 
                      Calcula e exibe o total de quilômetros.
                      Usa serviceExecution se aprovado, senão usa service original.
                    */}
										<View
											style={[
												{
													flex: 1,
													justifyContent: 'center',
													paddingTop: 5,
													paddingBottom: 5,
													paddingLeft: 5,
													paddingRight: 5,
													backgroundColor: '#FFFFFF',
												},
											]}
										>
											<Text style={styles.tableCellCenter}>
												{calculateKmsTotal(!isTimesheetApproved ? service : (serviceExecution as ServiceExecution))}
											</Text>
										</View>
										{/* 
                      Exibe KMs separados por modo: carga (false) e vazio (true).
                      Primeira linha: KMs com carga, Segunda linha: KMs vazio.
                    */}
										<View
											style={[
												{
													flex: 1,
													justifyContent: 'center',
													paddingTop: 5,
													paddingBottom: 5,
													paddingLeft: 5,
													paddingRight: 5,
													backgroundColor: '#FFFFFF',
												},
											]}
										>
											<Text style={styles.tableCellRight}>
												{calculateKmsByMode(
													!isTimesheetApproved ? service : (serviceExecution as ServiceExecution),
													false,
												)}
												{'\n'}
												{calculateKmsByMode(
													!isTimesheetApproved ? service : (serviceExecution as ServiceExecution),
													true,
												)}
											</Text>
										</View>
										{/* 
                      Exibe número de passageiros.
                      Se não aprovado, usa dados do serviço original.
                      Se aprovado, usa dados da execução ou '-' se não disponível.
                    */}
										<View
											style={[
												{
													flex: 1,
													justifyContent: 'center',
													paddingTop: 5,
													paddingBottom: 5,
													paddingLeft: 5,
													paddingRight: 5,
													backgroundColor: '#FFFFFF',
												},
											]}
										>
											<Text style={styles.tableCellCenter}>
												{!isTimesheetApproved ? service.pax : serviceExecution?.pax ?? '-'}
											</Text>
										</View>
										{/* 
                      Exibe status de limpeza do veículo.
                      Usa o serviço apropriado baseado no status de aprovação.
                    */}
										<View
											style={[
												{
													flex: 1,
													justifyContent: 'center',
													paddingTop: 5,
													paddingBottom: 5,
													paddingLeft: 5,
													paddingRight: 5,
													backgroundColor: '#FFFFFF',
												},
												styles.tableCellLast,
											]}
										>
											<Text style={styles.tableCellCenter}>
												{(!isTimesheetApproved ? service : serviceExecution)?.isClean ? 'Sim' : 'Não'}
											</Text>
										</View>
									</View>
									{hasComments && (
										<View
											style={isLastRow ? styles.tableRowLast : styles.tableRow}
											wrap={false}
										>
											<Text style={[styles.tableCell, styles.notasAdicionais, { flex: 9 }, styles.tableCellLast]}>
												<Text style={{ fontWeight: 'bold' }}>Adicionais:</Text>{' '}
												{!isTimesheetApproved ? service.comments : serviceExecution?.comments ?? ''}
											</Text>
										</View>
									)}
								</React.Fragment>
							)
						})}
					</View>
				</View>

				{/* Seção Unificada: Folha de Ponto, Regime, Abonos e Notas */}
				<View
					style={[
						styles.unifiedSection,
						{
							borderBottomWidth: hasEscaladorComments ? 0.5 : 0,
							paddingBottom: hasEscaladorComments ? 2 : 0,
						},
					]}
				>
					{/* Folha de Ponto Section */}
					<View style={styles.unifiedSectionItem}>
						<Text style={[styles.sectionTitle, { marginTop: 15 }]}>Folha de Ponto</Text>
						<View style={[styles.table]}>
							{/* Table Header */}
							<View
								style={styles.tableRow}
								wrap={false}
							>
								<Text style={[styles.tableHeader, { flex: 1 }]}>Período</Text>
								<Text style={[styles.tableHeader, { flex: 1 }]}>Hora Início</Text>
								<Text style={[styles.tableHeader, { flex: 1 }]}>Data Início</Text>
								<Text style={[styles.tableHeader, { flex: 1 }]}>Hora Fim</Text>
								<Text style={[styles.tableHeader, { flex: 1 }]}>Data Fim</Text>
								<Text style={[styles.tableHeader, { flex: 1 }]}>Kms</Text>
								<Text style={[styles.tableHeader, { flex: 1 }, styles.tableHeaderLast]}>Limpeza</Text>
							</View>
							{/* Table Rows */}
							{/* 
                Exibe o status inicial da jornada (registro de início do trabalho).
                Mostra data/hora de início, quilometragem inicial e status de limpeza.
              */}
							{timesheetPage.services && (
								<View
									style={styles.tableRow}
									wrap={false}
								>
									<Text style={[styles.tableCell, { flex: 1 }]}>Início da jornada</Text>
									<Text style={[styles.tableCell, { flex: 1 }]}>
										{dayjs(
											timesheetPage.services[0]?.serviceExecution
												? timesheetPage?.services[0]?.serviceExecution?.startDateTime
												: timesheetPage?.services[0]?.service?.startDateTime,
										)?.format('HH:mm')}
									</Text>
									<Text style={[styles.tableCell, { flex: 1 }]}>
										{dayjs(
											timesheetPage.services[0]?.serviceExecution
												? timesheetPage?.services[0]?.serviceExecution?.startDateTime
												: timesheetPage?.services[0]?.service?.startDateTime,
										).format('DD/MM/YYYY')}
									</Text>
									<Text style={[styles.tableCell, { flex: 1 }]}>{'-'}</Text>
									<Text style={[styles.tableCell, { flex: 1 }]}>{'-'}</Text>
									<Text style={[styles.tableCell, { flex: 1 }]}>
										{timesheetPage.services[0]?.serviceExecution
											? Math.min(
													...(timesheetPage?.services[0]?.serviceExecution?.kmsVehicles?.map(kv => kv.kmsIni) ?? []),
												)
											: Math.min(...(timesheetPage?.services[0]?.service?.kmsVehicles?.map(kv => kv.kmsIni) ?? []))}
									</Text>
									<Text style={[styles.tableCell, { flex: 1 }]}>
										{timesheetPage.initialStatus && (timesheetPage.initialStatus.isClean ? 'Sim' : 'Não')}
									</Text>
								</View>
							)}
							{/* 
                Mapeia e exibe todos os períodos de trabalho registrados.
                Cada item representa um período de trabalho com início e fim.
              */}
							{renderWorkTimesRows(
								!isTimesheetApproved
									? timesheetPage.workTimes
									: timesheetPage.approval?.timeSheetApprovalWorkTimes ?? [],
							)}
						</View>
						{timesheetPage.services && (
							<View
								style={styles.tableRow}
								wrap={false}
							>
								<Text style={[styles.tableCell, { flex: 1 }]}>Fim da jornada</Text>
								<Text style={[styles.tableCell, { flex: 1 }]}>{'-'}</Text>
								<Text style={[styles.tableCell, { flex: 1 }]}>{'-'}</Text>
								<Text style={[styles.tableCell, { flex: 1 }]}>
									{dayjs(
										timesheetPage.services[timesheetPage.services.length - 1]?.serviceExecution
											? timesheetPage?.services[timesheetPage.services.length - 1]?.serviceExecution?.endDateTime
											: timesheetPage?.services[timesheetPage.services.length - 1]?.service?.endDateTime,
									)?.format('HH:mm')}
								</Text>
								<Text style={[styles.tableCell, { flex: 1 }]}>
									{dayjs(
										timesheetPage.services[timesheetPage.services.length - 1]?.serviceExecution
											? timesheetPage?.services[timesheetPage.services.length - 1]?.serviceExecution?.endDateTime
											: timesheetPage?.services[timesheetPage.services.length - 1]?.service?.endDateTime,
									).format('DD/MM/YYYY')}
								</Text>
								<Text style={[styles.tableCell, { flex: 1 }]}>
									{timesheetPage.services[0]?.serviceExecution
										? Math.max(
												...(timesheetPage?.services[0]?.serviceExecution?.kmsVehicles?.map(kv => kv.kmsEnd) ?? []),
											)
										: Math.max(...(timesheetPage?.services[0]?.service?.kmsVehicles?.map(kv => kv.kmsEnd) ?? []))}
								</Text>
								<Text style={[styles.tableCell, { flex: 1 }]}>
									{timesheetPage.initialStatus && (timesheetPage.initialStatus.isClean ? 'Sim' : 'Não')}
								</Text>
							</View>
						)}
						<Text
							style={styles.totalHoras}
							wrap={false}
						>
							Total de Horas: <Text style={styles.totalHorasValue}>{totalHorasFormatado}</Text>
						</Text>
					</View>

					{/* Regime de Trabalho Section */}
					<View
						style={[styles.unifiedSectionItem, { marginTop: 15 }]}
						wrap={false}
					>
						<Text style={styles.sectionTitleRegime}>Regime de Trabalho e Agente Único</Text>
						<View style={styles.regimeInfo}>
							<Text style={styles.regimeItem}>
								Regime de Trabalho: <Text style={styles.regimeItemValue}>{getWorktype()}</Text>
							</Text>
							<Text style={[styles.regimeItem, { marginLeft: 30 }]}>
								Agente Único: <Text style={styles.regimeItemValue}>{agenteUnico}</Text>
							</Text>
						</View>
					</View>

					{/* Abonos e Refeições Section */}
					<View
						style={[styles.unifiedSectionItem, { marginTop: 15 }]}
						wrap={false}
					>
						<Text style={styles.sectionTitleRegime}>Abonos e Refeições Aplicáveis</Text>
						<View style={styles.abonosContent}>
							{renderBonusCodes(
								!isTimesheetApproved ? timesheetPage.bonusCodes : timesheetPage.approval?.timeSheetApprovalCodes ?? [],
							)}
							<View style={styles.outrosAbonos}>
								{/* 
                  Processa e exibe outros abonos (gratificações) da empresa.
                  Os dados vêm como JSON string em timesheetPage.approval.refunds.
                  Faz o parse, filtra apenas valores > 0, e faz match com companyGratifications
                  usando o campo campoSP para obter a descrição.
                */}
								{timesheetPage.approval?.refunds && companyGratifications
									? (() => {
											try {
												// Faz parse do JSON string de refunds
												const refundsData = JSON.parse(timesheetPage.approval.refunds) as Array<{
													value: number
													spField: string
													id: number
												}>
												// Filtra refunds com valor > 0 e faz match com gratificações da empresa
												const gratifications = refundsData
													.filter(refund => refund.value > 0)
													.map(refund => {
														// Busca a gratificação correspondente pelo campoSP
														const gratification = companyGratifications.find(grat => grat.campoSP === refund.spField)
														return gratification
															? {
																	id: gratification.id,
																	descricao: gratification.descricao,
																	valor: refund.value,
																}
															: null
													})
													// Remove valores null do array
													.filter(
														(
															grat,
														): grat is {
															id: number
															descricao: string
															valor: number
														} => grat !== null,
													)

												// Renderiza apenas se houver gratificações válidas
												return gratifications.length > 0 ? (
													<Text style={styles.abonoItem}>
														Outros Abonos:{'  '}
														{gratifications.map((grat, index) => (
															<React.Fragment key={grat.id}>
																<Text style={styles.abonoValue}>
																	{grat.descricao}: €{grat.valor.toFixed(2).replace('.', ',')}
																</Text>
																{index < gratifications.length - 1 && ' '}
															</React.Fragment>
														))}
													</Text>
												) : null
											} catch (error) {
												// Em caso de erro no parse do JSON, loga e retorna null
												console.error('Error parsing refunds JSON:', error)
												return null
											}
										})()
									: null}
							</View>
						</View>
					</View>

					{/* Notas Adicionais Section */}
					<View
						style={[styles.unifiedSectionItem, { marginTop: 15, marginBottom: hasEscaladorComments ? 15 : 0 }]}
						wrap={false}
					>
						<Text style={styles.sectionTitleRegime}>Notas Adicionais</Text>
						<View>
							<Text style={styles.notasContent}>
								Notas Adicionais: <Text style={styles.notasValue}>{timesheetPage?.comments}</Text>
							</Text>
						</View>
					</View>
				</View>

				{/* Comentários do Escalador Section */}
				{hasEscaladorComments && (
					<View
						style={[styles.section, styles.minHeightSection, { marginTop: 15 }]}
						wrap={false}
					>
						<Text style={styles.sectionTitle}>Comentários do Escalador à Folha de Ponto</Text>
						<View>
							<Text style={styles.comentariosContent}>
								Comentários: <Text style={styles.comentariosValue}>{timesheetPage.approval?.comments ?? ''}</Text>
							</Text>
						</View>
					</View>
				)}

				{/* Footer com numeração de páginas */}
				<Text
					style={styles.footer}
					render={({ pageNumber, totalPages }) => `Página ${pageNumber} de ${totalPages}`}
					fixed
				/>
			</Page>
		</Document>
	)
}

/**
 * Hook personalizado para coletar e preparar dados do Redux para o componente de impressão.
 * Extrai informações da folha de ponto, serviços originais, serviços de execução e aprovações.
 * Filtra e mapeia os dados para garantir que apenas valores válidos sejam retornados.
 *
 * @returns Objeto contendo:
 *   - timesheetPage: Estado completo da folha de ponto
 *   - servicesOriginal: Array de serviços originais escalados
 *   - servicesExecution: Array de serviços de execução
 *   - servicesApproval: Array de serviços de aprovação
 *   - isTimesheetApproved: Flag indicando se a folha de ponto está aprovada
 */
export const usePrintReportData = () => {
	const isTimesheetApproved = useIsTimesheetApproved()
	const timesheetPage = useSelector((state: TimesheetStoreState) => state.timesheetPage)
	const timesheetApprovalDetailsAccept = useSelector(
		(state: TimesheetStoreState) => state.timesheetApprovalDetailsAccept,
	)

	// Extrai apenas os serviços originais (escalados) do estado
	// Filtra valores null/undefined para garantir array limpo
	const servicesOriginal: ServiceOriginal[] = timesheetPage?.services
		.map(service => service.service)
		.filter(Boolean) as ServiceOriginal[]

	// Extrai apenas os serviços de execução do estado
	// Filtra valores null/undefined para garantir array limpo
	const servicesExecution: ServiceExecution[] = timesheetPage?.services
		.map(service => service.serviceExecution)
		.filter(Boolean) as ServiceExecution[]

	// Extrai os serviços de aprovação do estado de aprovação
	// Filtra valores null/undefined para garantir array limpo
	const servicesApproval: ServiceExecutionUpdateRequest[] = timesheetApprovalDetailsAccept?.services
		.map(service => service)
		.filter(Boolean) as ServiceExecutionUpdateRequest[]

	return {
		timesheetPage,
		servicesOriginal,
		servicesExecution,
		servicesApproval,
		isTimesheetApproved,
	}
}
